import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, CircleUserRound, ChevronUp, ChevronDown, Star, Loader2 } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useCart } from 'contexts/CartContext';
import { useCurrency } from 'contexts/CurrencyContext';
import { NAVIGATION_ITEMS, ROUTES, CURRENCIES } from 'config';
import { useLoginModal } from 'contexts/LoginModalContext';
import { useCategories } from 'components/sections/shop/hooks';
import LazyImage from 'components/ui/LazyImage';
import { productService, type ProductListItem } from 'services/product.service';
import { deliveryService } from 'services/delivery.service';
import { getProductImage, getProductPrice, getProductVatPrice } from 'components/sections/shop/utils/helperFn';
import { createProductSlug, createSlug, createCategorySlug, formatPrice, type CurrencyCode } from 'utils/HelperFn';
import { useProduct } from 'contexts/ProductContext';
import { partitionShopCategories } from 'components/sections/shop/utils/partitionShopCategories';

// Product categories for the mega menu
const PRODUCT_CATEGORIES = [
  '3D Print Resin',
  'Acrylic Powders',
  'Acrylic Teeth',
  'Casting Alloys',
  'Composite Material',
  'Die Stone',
  'Dentist',
  'Digital Equipment',
  'Hand Tools',
  'Investment',
  'Laboratory Accessories',
  'Laboratory Equipment',
  'Milling',
  'Mouthguard Material',
  'Plaster',
  'Polishing & Finishing',
  'Rotary',
  'Separation Fluid',
  'Waxes'
];

// Laboratory types for the mega menu
const LABORATORY_TYPES = [
  'Clinical Dental Technician',
  'Cobalt Chrome',
  'Colleges',
  'Crown & Bridge',
  'Hospitals',
  'Orthodontics',
  'Prosthetics',
  'Universities'
];

// Header spans the full viewport width, so it uses its own padded container
// instead of the centred `container-custom` used elsewhere on the site.
const HEADER_CONTAINER = 'w-full px-4 sm:px-6 lg:px-8';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { itemCount } = useCart();
  const { selectedCurrency, setCurrency, currencyId } = useCurrency();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();
  const { setSlugMapping } = useProduct();
  const { openLoginModal } = useLoginModal();

  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isMobileShopMenuOpen, setIsMobileShopMenuOpen] = useState(false);
  // Label of the nested nav dropdown currently open (desktop hover / mobile toggle)
  const [openNavDropdown, setOpenNavDropdown] = useState<string | null>(null);
  const [openMobileNavDropdown, setOpenMobileNavDropdown] = useState<string | null>(null);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  // Search dropdown state
  const [searchResults, setSearchResults] = useState<ProductListItem[]>([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  // Admin-configured free-shipping thresholds for the promo banner (default until loaded).
  const [freeShippingThresholds, setFreeShippingThresholds] = useState({
    freeShippingThresholdGbp: 160,
    freeShippingThresholdEur: 185,
  });
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const mobileSearchDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const searchIdRef = useRef(0);

  const { productCategories: apiProductCategories, laboratoryTypes: apiLaboratoryTypes } = useMemo(
    () => partitionShopCategories(categories ?? []),
    [categories]
  );

  // Create fallback categories
  const fallbackProductCategories = useMemo(() => {
    return PRODUCT_CATEGORIES.map((name, index) => ({
      id: `fallback-product-${index}`,
      name,
      path: `Product Category > ${name}`,
    }));
  }, []);

  const fallbackLaboratoryTypes = useMemo(() => {
    return LABORATORY_TYPES.map((name, index) => ({
      id: `fallback-lab-${index}`,
      name,
      path: `Laboratory Type > ${name}`,
    }));
  }, []);

  // Use API categories if available and successful, otherwise use fallback
  // Only use fallback when API fails (error or empty response), not during loading
  const productCategories = useMemo(() => {
    // While loading, return empty array (don't use fallback)
    if (categoriesLoading) {
      return [];
    }
    // On error or if API returned empty, use fallback
    if (categoriesError || apiProductCategories.length === 0) {
      return fallbackProductCategories;
    }
    // If API returned categories, use them
    return apiProductCategories;
  }, [apiProductCategories, categoriesError, categoriesLoading, fallbackProductCategories]);

  const laboratoryTypes = useMemo(() => {
    // While loading, return empty array (don't use fallback)
    if (categoriesLoading) {
      return [];
    }
    // On error or if API returned empty, use fallback
    if (categoriesError || apiLaboratoryTypes.length === 0) {
      return fallbackLaboratoryTypes;
    }
    // If API returned categories, use them
    return apiLaboratoryTypes;
  }, [apiLaboratoryTypes, categoriesError, categoriesLoading, fallbackLaboratoryTypes]);

  // Debounced search function with request cancellation and stale-response handling
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchTotalCount(0);
      setIsSearchDropdownOpen(false);
      return;
    }

    // Abort previous search if in flight
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    searchAbortControllerRef.current = controller;
    const currentSearchId = ++searchIdRef.current;

    setIsSearchLoading(true);
    try {
      const response = await productService.getProducts(
        {
          search: query.trim(),
          page: 1,
          size: 5, // Show only 5 results in dropdown
          currencyId,
        },
        controller.signal
      );
      // Ignore stale response if user typed again
      if (currentSearchId !== searchIdRef.current) return;
      setSearchResults(response.items);
      setSearchTotalCount(response.pagination.totalCount);
      setIsSearchDropdownOpen(true);
    } catch (error) {
      // Ignore aborted requests - a newer search superseded this one
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Search error:', error);
      if (currentSearchId !== searchIdRef.current) return;
      setSearchResults([]);
      setSearchTotalCount(0);
    } finally {
      // Only clear loading if this was the latest search
      if (currentSearchId === searchIdRef.current) {
        setIsSearchLoading(false);
      }
    }
  }, [currencyId]);

  // Handle search input change with debouncing
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setIsSearchDropdownOpen(false);
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Handle product click in search results
  const handleProductClick = (product: ProductListItem) => {
    const productSlug = createProductSlug(product.variantId, product.variantDescription);
    const categorySlug = product.categories && product.categories.length > 0
      ? createCategorySlug(product.categories[0].name)
      : 'uncategorized';
    setSlugMapping(productSlug, product.variantId);
    setIsSearchDropdownOpen(false);
    setSearchQuery('');
    navigate(`/product-category/${categorySlug}/${productSlug}`);
  };

  // Handle "View All Products" click
  const handleViewAllClick = () => {
    setIsSearchDropdownOpen(false);
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate(ROUTES.SHOP);
    }
  };

  // Load admin-configured free-shipping thresholds once for the promo banner.
  useEffect(() => {
    let active = true;
    void deliveryService.getFreeShippingThresholds().then((thresholds) => {
      if (active) setFreeShippingThresholds(thresholds);
    });
    return () => {
      active = false;
    };
  }, []);

  // Close currency dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(target)) {
        setIsCurrencyDropdownOpen(false);
      }

      const clickedInsideDesktopSearch = searchDropdownRef.current?.contains(target);
      const clickedInsideMobileSearch = mobileSearchDropdownRef.current?.contains(target);
      if (!clickedInsideDesktopSearch && !clickedInsideMobileSearch) {
        setIsSearchDropdownOpen(false);
      }
    };

    if (isCurrencyDropdownOpen || isSearchDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCurrencyDropdownOpen, isSearchDropdownOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Close search dropdown on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isSearchDropdownOpen) {
        setIsSearchDropdownOpen(false);
      }
    };

    if (isSearchDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isSearchDropdownOpen]);

  // Account credit figures are held in the account's own OrderWise currency
  // (not always GBP), which the backend sends alongside them. They are
  // deliberately independent of the display currency selector.
  //
  // Only account customers with a limit see this: the backend defaults
  // ccd_credit_limit to 0, so card-only customers would otherwise be shown a
  // meaningless "£0.00 / £0.00". On-hold accounts still see their figures —
  // greyed out behind an "On hold" badge — since they cannot order on account.
  const creditInfo = useMemo(() => {
    if (!isAuthenticated || !user) return null;
    if (!user.accountCustomer || !user.creditLimit || user.creditLimit <= 0) return null;

    const availableToSpend = user.availableToSpend ?? 0;
    const code = user.creditCurrencyCode || 'GBP';
    return {
      availableToSpend,
      creditLimit: user.creditLimit,
      symbol: user.creditCurrencySymbol || '£',
      // formatPrice only uses the code to pick a locale; anything other than
      // EUR formats fine under en-GB, so non-EUR accounts (CHF, JPY) fall back
      // to it while still showing their own symbol.
      locale: (code === 'EUR' ? 'EUR' : 'GBP') as CurrencyCode,
      onHold: Boolean(user.onHold),
      // At or below zero there is nothing left to spend on account.
      depleted: availableToSpend <= 0,
    };
  }, [isAuthenticated, user]);

  const renderCreditInfo = (isMobile = false) => {
    if (!creditInfo) return null;

    const { availableToSpend, creditLimit, symbol, locale, onHold, depleted } = creditInfo;
    const availableValueClass = onHold || depleted ? 'text-amber-700' : 'text-gray-900';

    return (
      <div
        className={
          isMobile
            ? `flex items-center justify-between gap-4 rounded-lg px-4 py-3 mb-4 ${onHold ? 'bg-amber-50' : 'bg-primary-50'}`
            : `hidden lg:flex items-center divide-x rounded-lg px-3 py-1.5 whitespace-nowrap ${onHold ? 'bg-amber-50 divide-amber-200' : 'bg-white/70 divide-gray-200'}`
        }
      >
        <div className={isMobile ? '' : 'pr-3'}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 leading-none">
            Available to spend
          </div>
          <div className={`text-sm font-semibold tabular-nums leading-tight mt-1 ${availableValueClass}`}>
            {formatPrice(availableToSpend, symbol, locale)}
          </div>
        </div>

        <div className={isMobile ? '' : 'pl-3'}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 leading-none">
            Credit limit
          </div>
          <div className="text-sm font-semibold text-gray-900 tabular-nums leading-tight mt-1">
            {formatPrice(creditLimit, symbol, locale)}
          </div>
        </div>

        {onHold && (
          <div className={isMobile ? '' : 'pl-3'}>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              On hold
            </span>
          </div>
        )}
      </div>
    );
  };

  const handleCurrencySelect = (currency: typeof CURRENCIES[number]) => {
    setCurrency(currency);
    setIsCurrencyDropdownOpen(false);
  };

  const renderSearchDropdown = (isMobile = false) => (
    <div
      className={`absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-hidden flex flex-col ${
        isMobile
          ? 'left-1/2 -translate-x-1/2 w-[92vw] max-w-[560px]'
          : 'left-auto right-0 w-[420px] lg:w-[560px]'
      }`}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Products</h3>
        </div>

        {isSearchLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
            <p>Searching...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {searchResults.map((product) => {
              const imageUrl = getProductImage(product);
              const price = getProductPrice(product, selectedCurrency.symbol, selectedCurrency.code);
              const vatPrice = getProductVatPrice(product, selectedCurrency.symbol, selectedCurrency.code);
              const productName = product.variantDescription || product.productDescription || 'Product';
              const rating = 0;
              const reviewCount = 0;

              return (
                <div
                  key={product.variantId}
                  onClick={() => handleProductClick(product)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-start space-x-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                    {imageUrl ? (
                      <LazyImage
                        src={imageUrl}
                        alt={productName}
                        className="w-full h-full object-cover"
                        sizes="48px"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200"></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate mb-1">
                      {productName}
                    </h4>
                    <div className="text-sm text-gray-600 mb-1">
                      {price} <span className="text-xs">Ex VAT</span>
                      {product.price?.rspIncVat && (
                        <> ({vatPrice} <span className="text-xs">Inc VAT</span>)</>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={12}
                          fill={i < rating ? 'currentColor' : 'none'}
                          className={i < rating ? 'text-yellow-400' : 'text-gray-300'}
                        />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({reviewCount})</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : searchQuery.trim() && !isSearchLoading ? (
          <div className="p-8 text-center text-gray-500">
            <p>No products found</p>
          </div>
        ) : null}
      </div>

      {searchTotalCount > searchResults.length && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleViewAllClick}
            className="text-primary-600 hover:text-primary-700 font-medium text-sm w-full text-right"
          >
            View All {searchTotalCount} Products &gt;
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Top Bar */}
      <div className="bg-primary-900 text-white py-2 relative overflow-visible">
        <div className={`${HEADER_CONTAINER} overflow-visible`}>
          <div className="flex justify-between items-center text-sm relative">
            <div className="flex items-center space-x-4 relative">
              {/* Currency Selector */}
              <div className="relative" ref={currencyDropdownRef}>
                <button
                  onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                  className="flex items-center space-x-1 text-white hover:text-gray-200 transition-colors"
                  aria-label="Select currency"
                >
                  <span>{selectedCurrency.code}</span>
                  {isCurrencyDropdownOpen ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>

                {/* Currency Dropdown */}
                {isCurrencyDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] min-w-[150px] max-h-[300px] overflow-y-auto">
                    {CURRENCIES.map((currency) => (
                      <button
                        key={currency.code}
                        onClick={() => handleCurrencySelect(currency)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors flex items-center justify-between first:rounded-t-lg last:rounded-b-lg ${selectedCurrency.code === currency.code ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{currency.symbol}</span>
                          <span>{currency.code}</span>
                        </div>
                        {selectedCurrency.code === currency.code && (
                          <span className="text-primary-600 font-bold">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <span className="font-medium">FREE SHIPPING OVER {selectedCurrency.code === 'EUR' ? `${freeShippingThresholds.freeShippingThresholdEur}€` : `£${freeShippingThresholds.freeShippingThresholdGbp}`} EX VAT</span>
            <div className="flex items-center space-x-8">
              <a
                href="https://www.abbeyclearaligners.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-gray-200 transition-colors"
              >
                Dentist Portal
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-[#d2def1] sticky top-0 z-50">
        <div className={HEADER_CONTAINER}>
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to={ROUTES.HOME} className="flex items-center space-x-3">
              <LazyImage
                src="/images/logo.png"
                alt="Abbey Dental Supplies"
                className="w-24 lg:w-32"
                sizes="(max-width: 1023px) 96px, 128px"
              />
            </Link>

            {/* Mobile Search Bar */}
            <form onSubmit={handleSearch} className="lg:hidden flex-1 mx-3">
              <div className="relative" ref={mobileSearchDropdownRef}>
                <button
                  type="submit"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isSearchLoading}
                  aria-label="Search products"
                >
                  {isSearchLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Search size={16} />
                  )}
                </button>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchQuery.trim()) {
                      setIsSearchDropdownOpen(true);
                    }
                  }}
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                />

                {isSearchDropdownOpen && renderSearchDropdown(true)}
              </div>
            </form>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {NAVIGATION_ITEMS.map((item) => (
                <div key={item.path} className="relative">
                  {item.label === 'Shop' ? (
                    <div
                      className="relative group"
                      onMouseEnter={() => setIsShopMenuOpen(true)}
                      onMouseLeave={() => setIsShopMenuOpen(false)}
                    >
                      <div className="flex items-center space-x-1">
                        <Link
                          to={ROUTES.SHOP}
                          className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                        >
                          {item.label}
                        </Link>
                        <ChevronUp
                          size={16}
                          className={`text-gray-700 hover:text-primary-600 transition-transform ${isShopMenuOpen ? 'rotate-180' : ''} cursor-pointer`}
                          onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
                        />
                      </div>

                      {/* Shop Mega Menu - Inside the group container */}
                      {isShopMenuOpen && (
                        <div className="absolute top-full left-0 z-50 min-w-[600px] pt-8">
                          <div className="p-6 bg-white border-t rounded-lg border-gray-200 shadow-lg">
                            <div className="grid grid-cols-2 gap-12">
                              {/* Product Category Column */}
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Product Category</h3>
                                <div className="space-y-2">
                                  {productCategories.length > 0 ? (
                                    <>
                                      {productCategories.map((category) => (
                                        <Link
                                          key={category.id}
                                          to={`/shop?category=${createSlug(category.name)}`}
                                          className="block text-gray-700 hover:text-primary-600 transition-colors"
                                          onClick={() => setIsShopMenuOpen(false)}
                                        >
                                          {category.name}
                                        </Link>
                                      ))}
                                    </>
                                  ) : (
                                    <div className="text-gray-500 text-sm">No categories available</div>
                                  )}
                                </div>
                              </div>

                              {/* Laboratory Type Column */}
                              <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Laboratory Type</h3>
                                <div className="space-y-2">
                                  {laboratoryTypes.length > 0 ? (
                                    <>
                                      {laboratoryTypes.map((type) => (
                                        <Link
                                          key={type.id}
                                          to={`/shop?category=${createSlug(type.name)}`}
                                          className="block text-gray-700 hover:text-primary-600 transition-colors"
                                          onClick={() => setIsShopMenuOpen(false)}
                                        >
                                          {type.name}
                                        </Link>
                                      ))}
                                      {categoriesLoading && (
                                        <div className="text-xs text-gray-400 italic mt-2">
                                          Updating from API...
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="text-gray-500 text-sm">No categories available</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : item.label === 'Special Offer' ? (
                    <Link
                      to={`${ROUTES.SHOP}?category=${createSlug('Special Offers')}`}
                      className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                    >
                      {item.label}
                    </Link>
                  ) : item.children ? (
                    <div
                      className="relative"
                      onMouseEnter={() => setOpenNavDropdown(item.label)}
                      onMouseLeave={() => setOpenNavDropdown(null)}
                    >
                      <div className="flex items-center space-x-1">
                        <Link
                          to={item.path}
                          className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                        >
                          {item.label}
                        </Link>
                        <ChevronUp
                          size={16}
                          className={`text-gray-700 hover:text-primary-600 transition-transform ${openNavDropdown === item.label ? 'rotate-180' : ''} cursor-pointer`}
                          onClick={() =>
                            setOpenNavDropdown(openNavDropdown === item.label ? null : item.label)
                          }
                        />
                      </div>

                      {openNavDropdown === item.label && (
                        <div className="absolute top-full left-0 z-50 min-w-[200px] pt-8">
                          <div className="p-4 bg-white border-t rounded-lg border-gray-200 shadow-lg space-y-2">
                            {item.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                className="block text-gray-700 hover:text-primary-600 transition-colors whitespace-nowrap"
                                onClick={() => setOpenNavDropdown(null)}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-md mx-8">
              <div className="relative w-full" ref={searchDropdownRef}>
                <button
                  type="submit"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                  disabled={isSearchLoading}
                >
                  {isSearchLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Search size={20} />
                  )}
                </button>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchQuery.trim() && searchResults.length > 0) {
                      setIsSearchDropdownOpen(true);
                    }
                  }}
                  className="w-full pl-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-transparent"
                />

                {/* Search Dropdown */}
                {isSearchDropdownOpen && renderSearchDropdown(false)}
              </div>
            </form>

            {/* Account Credit Summary */}
            {renderCreditInfo(false)}

            {/* User Actions */}
            <div className="flex items-center space-x-3 lg:space-x-4 lg:ml-4">
              {/* Cart */}
              <Link to={ROUTES.CART} className="relative text-gray-700 hover:text-primary-600 transition-colors">
                <ShoppingBag size={24} />
                {itemCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-primary-600 text-white text-[10px] font-semibold leading-none rounded-full px-1.5 py-1 min-w-[1.25rem] flex items-center justify-center tabular-nums">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <Link to={ROUTES.ACCOUNT} className="text-gray-700 hover:text-primary-600 transition-colors">
                    <CircleUserRound size={24} />
                  </Link>
                  {/* Dropdown menu would go here */}
                </div>
              ) : (
                <button
                  onClick={() => openLoginModal()}
                  className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  <CircleUserRound size={24} />
                </button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden text-gray-700 hover:text-primary-600 transition-colors"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>



        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className={`${HEADER_CONTAINER} py-4 pb-8`}>
              {/* Account Credit Summary */}
              {renderCreditInfo(true)}

              <nav className="flex flex-col space-y-4">
                {NAVIGATION_ITEMS.map((item) => (
                  <div key={item.path}>
                    {item.label === 'Shop' ? (
                      <div>
                        <div className="flex items-center justify-between w-full">
                          <Link
                            to={ROUTES.SHOP}
                            className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                          <button
                            onClick={() => setIsMobileShopMenuOpen(!isMobileShopMenuOpen)}
                            className="text-gray-700 hover:text-primary-600 transition-colors"
                            aria-label="Toggle shop menu"
                          >
                            <ChevronUp
                              size={16}
                              className={`transition-transform ${isMobileShopMenuOpen ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>

                        {/* Mobile Shop Submenu */}
                        {isMobileShopMenuOpen && (
                          <div className="ml-4 mt-2 space-y-4 max-h-[60vh] overflow-y-auto">
                            {/* Product Categories */}
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Product Category</h4>
                              <div className="space-y-1">
                                {productCategories.length > 0 ? (
                                  <>
                                    {productCategories.map((category) => (
                                      <Link
                                        key={category.id}
                                        to={`/shop?category=${createSlug(category.name)}`}
                                        className="block text-gray-600 hover:text-primary-600 transition-colors text-sm"
                                        onClick={() => {
                                          setIsMobileMenuOpen(false);
                                          setIsMobileShopMenuOpen(false);
                                        }}
                                      >
                                        {category.name}
                                      </Link>
                                    ))}
                                    {categoriesLoading && (
                                      <div className="text-xs text-gray-400 italic mt-1">
                                        Updating from API...
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-gray-500 text-sm">No categories available</div>
                                )}
                              </div>
                            </div>

                            {/* Laboratory Types */}
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Laboratory Type</h4>
                              <div className="space-y-1">
                                {laboratoryTypes.length > 0 ? (
                                  <>
                                    {laboratoryTypes.map((type) => (
                                      <Link
                                        key={type.id}
                                        to={`/shop?category=${createSlug(type.name)}`}
                                        className="block text-gray-600 hover:text-primary-600 transition-colors text-sm"
                                        onClick={() => {
                                          setIsMobileMenuOpen(false);
                                          setIsMobileShopMenuOpen(false);
                                        }}
                                      >
                                        {type.name}
                                      </Link>
                                    ))}
                                    {categoriesLoading && (
                                      <div className="text-xs text-gray-400 italic mt-1">
                                        Updating from API...
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-gray-500 text-sm">No categories available</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : item.label === 'Special Offer' ? (
                      <Link
                        to={`${ROUTES.SHOP}?category=${createSlug('Special Offers')}`}
                        className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ) : item.children ? (
                      <div>
                        <div className="flex items-center justify-between w-full">
                          <Link
                            to={item.path}
                            className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                          <button
                            onClick={() =>
                              setOpenMobileNavDropdown(
                                openMobileNavDropdown === item.label ? null : item.label
                              )
                            }
                            className="text-gray-700 hover:text-primary-600 transition-colors"
                            aria-label={`Toggle ${item.label} menu`}
                          >
                            <ChevronUp
                              size={16}
                              className={`transition-transform ${openMobileNavDropdown === item.label ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>

                        {openMobileNavDropdown === item.label && (
                          <div className="ml-4 mt-2 space-y-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.path}
                                to={child.path}
                                className="block text-gray-600 hover:text-primary-600 transition-colors text-sm"
                                onClick={() => {
                                  setIsMobileMenuOpen(false);
                                  setOpenMobileNavDropdown(null);
                                }}
                              >
                                {child.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header; 